import Layout from 'components/layout';

import React from 'react';

import { getI18nData, localizePhoneNumbers, getProcessedValue, getEntityFields } from '@lucid/utils';
import { graphql } from 'gatsby';
import {
  Box,
  ComponentImage,
  ComponentLink,
  Container,
  Heading,
  Hero,
  Icon,
  Link,
  linkParser,
} from '@lucid/components-react';
import ReactHtmlParser from 'react-html-parser';
import { Drupal_NodeWhitePaper, Drupal_ParagraphTopHeroWithLogo } from 'generated/drupal-graphql';
import { mqMax, LucidTheme } from '@lucid/theme';
import styled from '@emotion/styled';

import SignUpCta from 'components/sign-up-cta/sign-up-cta';
import { PageTemplateProps } from 'types/types';

const WhitePaper = ({ data, pageContext, location }: PageTemplateProps) => {
  const { drupal, i18nData } = data;
  const node = drupal.nodeById as Drupal_NodeWhitePaper;
  const { entityMetatags, entitySchemaMetatags, entityTranslations } = getEntityFields(node);
  const { langPrefix, pageType, nid, i18nKey, shouldHideSsr } = pageContext;
  const { body, fieldHeroSection, fieldHasWhitepaperCta, fieldHasRssLink, fieldHasFooter, fieldHasNavbar, fieldTitle } =
    node;

  const i18n = getI18nData(i18nData, i18nKey);

  const buttonGroupMx = 15;
  const buttonGroupMy = 10;
  const ButtonGroup = styled(Box)({
    display: 'inline-flex',
    flexWrap: 'wrap',
    margin: `-${buttonGroupMy}px 0 0 -${buttonGroupMx}px`,
    width: `calc(100% + ${buttonGroupMx}px)`,
    '> a.link': { margin: `${buttonGroupMy}px 0 0 ${buttonGroupMx}px` },
  });

  const heroSection = fieldHeroSection?.entity as Drupal_ParagraphTopHeroWithLogo;

  return (
    <Layout
      entityTranslations={entityTranslations}
      hasFooter={fieldHasFooter}
      hasNavbar={fieldHasNavbar}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
      schema={entitySchemaMetatags}
      shouldHideSsr={shouldHideSsr}
    >
      <Container
        css={{
          paddingTop: '40px',
          paddingBottom: '120px',
          [mqMax.computer]: {
            paddingTop: '40px',
            paddingBottom: '40px',
          },
          [mqMax.tabletSmall]: {
            paddingTop: '20px',
            paddingBottom: '20px',
          },
        }}
      >
        {heroSection?.fieldParagraph && (
          <Hero
            cta={
              {
                linkText: heroSection?.fieldCtaLink?.title,
                linkUrl: heroSection?.fieldCtaLink?.url?.path,
              } as ComponentLink
            }
            header={heroSection?.fieldTitle as string}
            image={
              {
                imageAlt: heroSection.fieldTitle,
                imageSrc: heroSection.fieldImageLink,
              } as ComponentImage
            }
            subheader={heroSection.fieldParagraph as string}
          />
        )}
        <Heading variant="headline3XL">{fieldTitle}</Heading>
        {fieldHasWhitepaperCta && (
          <Box as="section" className={`${pageType}__cta`}>
            <Heading as="h2" variant="headlineL">
              {i18n('mktg-ct-cs-cta-header')}
            </Heading>
            <ButtonGroup className={`${pageType}__cta-header-buttons`}>
              <SignUpCta>{i18n('mktg-ct-og-feature-paragraph-cta')}</SignUpCta>
              <Link displayAsButton to="/contact/get-a-quote">
                {i18n('mktg-ct-solutions-footer-cta-primary')}
              </Link>
            </ButtonGroup>
            <Box paddingY="20px">
              <Link className={`${pageType}__cta-phone`} to={'tel:+18772109890'}>
                {localizePhoneNumbers(
                  i18n('mktg-ct-solutions-footer-or-call', {
                    'sales-phone-number': '(877) 210 9890',
                  }),
                )}
              </Link>
            </Box>
          </Box>
        )}
        {body && (
          <Box
            className={`${pageType}__body`}
            css={(theme: LucidTheme) => ({
              width: '100%',
              [mqMax.mobile]: {
                wordBreak: 'break-word',
              },
              tbody: {
                minWidth: '500px',
                overflow: 'scroll',
              },
              th: {
                background: theme.colors.coolGrey[20],
                textAlign: 'left',
              },
              td: {
                border: `1px solid ${theme.colors.coolGrey[30]}`,
                padding: '0 10px',
                [mqMax.mobile]: {
                  fontSize: '0.5rem',
                  padding: '0 5px',
                  wordBreak: 'normal',
                },
              },
              a: {
                color: theme.colors.primary,
              },
              'ol, ul': {
                '> li': {
                  marginBottom: '10px',
                },
                'ul, ol': {
                  padding: `${theme.space[3]}px ${theme.space[5]}px`,
                },
              },
            })}
          >
            {ReactHtmlParser(getProcessedValue(body), {
              transform: linkParser,
            })}
          </Box>
        )}
        {fieldHasRssLink && (
          <Box as="section" className={`${pageType}__rss`}>
            <Heading as="h2" mt={6} variant="headlineL">
              {i18n('mtkg-whitepaper-rss')}
            </Heading>
            <Link displayAsButton newTab to="/legal-rss.xml">
              <Icon type="RSS" />
              &nbsp;&nbsp;RSS
            </Link>
          </Box>
        )}
        {fieldHasWhitepaperCta && (
          <Box as="section" className={`${pageType}__cta`}>
            <Heading as="h2" mt={6} variant="headlineL">
              {i18n('mktg-ct-cs-cta-header')}
            </Heading>
            <ButtonGroup className={`${pageType}__cta-header-buttons`}>
              <SignUpCta>{i18n('mktg-ct-og-feature-paragraph-cta')}</SignUpCta>
              <Link displayAsButton to="/contact/get-a-quote" variant="secondary">
                {i18n('mktg-ct-solutions-footer-cta-primary')}
              </Link>
            </ButtonGroup>
            <Box paddingY="20px">
              <Link className={`${pageType}__cta-phone`} to={'tel:+18772109890'}>
                {localizePhoneNumbers(
                  i18n('mktg-ct-solutions-footer-or-call', {
                    'sales-phone-number': '(877) 210 9890',
                  }),
                )}
              </Link>
            </Box>
          </Box>
        )}
      </Container>
    </Layout>
  );
};

export default WhitePaper;

export const query = graphql`
  query ($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeWhitePaper {
          ...entityFields
          fieldTitle
          fieldNavbarLink {
            title
            url {
              path
            }
          }
          fieldHeroSection {
            entity {
              ... on Drupal_ParagraphTopHeroWithLogo {
                fieldTitle
                fieldImageLink
                fieldParagraph
                fieldCtaLink {
                  title
                  url {
                    path
                  }
                }
              }
            }
          }
          body {
            processed
          }
          fieldHasWhitepaperCta
          fieldHasNavbar
          fieldHasFooter
          fieldHasRssLink
        }
      }
    }
    i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" }, name: { in: [$i18nKey, "en"] } }) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_cs_cta_header
            mktg_ct_og_feature_paragraph_cta
            mktg_ct_solutions_footer_cta_primary
            mktg_ct_solutions_footer_or_call
            mtkg_whitepaper_rss
          }
        }
      }
    }
  }
`;
